/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useMemo, useRef, useState } from 'react';
import { Ionicons } from '@expo/vector-icons';
import { ActivityIndicator, Platform, StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { CatalogStockProductSearchFields } from '../../shared/product-search/components/CatalogStockProductSearchFields';
import { MIN_PRODUCT_SEARCH_QUERY_CHARS } from '../../shared/product-search/productSearchUx';
import { searchProducts } from '../../catalog/infra-api';
import { spacing } from '../../shared/theme/spacing'
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors'
import { typography } from '../../shared/theme/typography';
import { PaperSheet } from '../../shared/ticket/PaperSheet'
import { TicketKpiStrip } from '../../shared/ticket/TicketKpiStrip'
import type { TicketKpiItem } from '../../shared/ticket/TicketKpiStrip'
import { TicketModuleHeader } from '../../shared/ticket/TicketModuleHeader'
import { TicketMoreActionsSheet } from '../../shared/ticket/TicketMoreActionsSheet'
import type { TicketMoreAction } from '../../shared/ticket/TicketMoreActionsSheet';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import type { InventoryDashboardRow } from '../../inventory/stock/public-api';
import type { ProductSearchRow as InventoryProductSearchRow } from '../../catalog/domain-api';
import type { StockStatusFilter, StockSort } from '../../inventory/stock/public-api';
import { useOperationalWebSearchFocus } from '../../shared/ui/hooks-screen/useOperationalWebSearchFocus';
import { useOperationalFilterLayout } from '../../shared/filters/useOperationalFilterLayout';
import { formatMoneyCompact } from '../../shared/format/moneyFormat';

type Props = {
  canEdit: boolean;
  inventoryDashboard: InventoryDashboardRow | null;
  searchQuery: string;
  lookupFeedback: string | null;
  onSearchQueryChange: (value: string) => void;
  onOpenScanner: () => void;
  onOpenAddStock: () => void;
  onOpenTransferInfo: () => void;
  /** False con una sola sucursal — deshabilita transferir con hint. */
  canTransferBetweenBranches?: boolean;
  onOpenPhysicalCount: () => void;
  onOpenBulkCoefficient: () => void;
  onOpenCsv?: () => void;
  showBarcodeCameraButton?: boolean;
  onSearchSubmitEditing?: () => void;
  autoFocusSearchOnWeb?: boolean;
  /** PC/tablet (>=768): KPI en 4 columnas y ticket bar (búsqueda + acciones en fila). */
  layoutWide?: boolean;
  branchId: string | null;
  filterTrigger: React.ReactNode;
  onPickStockSearchSuggestion?: (row: InventoryProductSearchRow) => void;
  /** Ítem elegido desde sugerencias; chip como Informes › Ventas */
  pinnedStockProduct?: { label: string } | null;
  onClearPinnedStockSearch?: () => void;
  onOpenActivity?: () => void;
  activityLoading?: boolean;
  canViewValuation?: boolean;
  /** Filtro de status actual (para KPIs filtrables). */
  stockFilter: StockStatusFilter;
  /** Toggle de filtro rápido desde KPI (si está activo, vuelve a 'all'). */
  onToggleStockFilter: (filter: StockStatusFilter) => void;
  /** True si el último refresh de KPIs falló — muestra aviso con retry. */
  kpisError?: boolean;
  /** True mientras carga el primer snapshot de KPIs. */
  kpisLoading?: boolean;
  /** Reintenta la carga de KPIs (`useInventoryKpis.refresh`). */
  onRetryKpis?: () => void;
};

export const InventoryHeaderSection = ({
  canEdit,
  inventoryDashboard,
  searchQuery,
  lookupFeedback,
  onSearchQueryChange,
  onOpenScanner,
  onOpenAddStock,
  onOpenTransferInfo,
  canTransferBetweenBranches = true,
  onOpenPhysicalCount,
  onOpenBulkCoefficient,
  onOpenCsv,
  showBarcodeCameraButton = true,
  onSearchSubmitEditing,
  autoFocusSearchOnWeb = false,
  layoutWide: layoutWideProp,
  branchId,
  filterTrigger,
  onPickStockSearchSuggestion,
  pinnedStockProduct = null,
  onClearPinnedStockSearch,
  onOpenActivity,
  activityLoading = false,
  canViewValuation = true,
  stockFilter,
  onToggleStockFilter,
  kpisError = false,
  kpisLoading = false,
  onRetryKpis,
}: Props) => {
  const { layoutWide: layoutWideCtx } = useOperationalFilterLayout();
  const layoutWide = layoutWideProp ?? layoutWideCtx;
  const searchInputRef = useRef<TextInput>(null);
  useOperationalWebSearchFocus(searchInputRef, {
    shortcutsEnabled: canEdit,
  });

  /** Contadores de vista rápida: siempre del dashboard de sucursal (no del listado filtrado/paginado). */
  const quickViewCountsReady = inventoryDashboard != null;
  const belowMin = inventoryDashboard?.belowMin ?? 0;
  const expiringSoon = inventoryDashboard?.expiringSoon ?? 0;
  const negative = inventoryDashboard?.negative ?? 0;
  const expired = inventoryDashboard?.expired ?? 0;
  const showExpiryTracking = expiringSoon + expired > 0;

  const totalProducts = inventoryDashboard?.totalProducts ?? 0;
  const totalValue = inventoryDashboard?.totalValue ?? 0;
  const coveragePercent = inventoryDashboard?.coveragePercent ?? 0;

  // Críticos = mismo criterio que el filtro 'negative' del listado:
  // productos con stock negativo y NO vencidos. Los vencidos tienen su propio
  // filtro ('expired') — antes el número sumaba ambos y nunca coincidía con la lista.
  const criticalCount = negative;

  const kpiItems = useMemo((): TicketKpiItem[] => {
    const items: TicketKpiItem[] = [
      { label: 'Productos', value: quickViewCountsReady ? String(totalProducts) : '—' },
      ...(canViewValuation
        ? [{ label: 'Valor', value: quickViewCountsReady ? formatMoneyCompact(totalValue) : '—' }]
        : []),
      { label: 'Cobertura', value: quickViewCountsReady ? `${coveragePercent.toFixed(0)}%` : '—' },
      {
        label: 'Críticos',
        value: quickViewCountsReady ? String(criticalCount) : '—',
        tone: quickViewCountsReady && criticalCount > 0 ? 'bad' : 'default',
        onPress: criticalCount > 0 ? () => onToggleStockFilter('negative') : undefined,
        selected: stockFilter === 'negative',
        accessibilityLabel: 'Críticos — filtrar stock negativo',
        testID: 'stock.kpi.critical',
      },
      {
        label: 'Stock bajo',
        value: quickViewCountsReady ? String(belowMin) : '—',
        tone: quickViewCountsReady && belowMin > 0 ? 'warn' : 'default',
        onPress: belowMin > 0 ? () => onToggleStockFilter('low') : undefined,
        selected: stockFilter === 'low',
        accessibilityLabel: 'Stock bajo — filtrar productos bajo el mínimo',
        testID: 'stock.kpi.low',
      },
    ];
    if (showExpiryTracking) {
      items.push(
        {
          label: 'Por vencer',
          value: quickViewCountsReady ? String(expiringSoon) : '—',
          tone: quickViewCountsReady && expiringSoon > 0 ? 'warn' : 'default',
          onPress: expiringSoon > 0 ? () => onToggleStockFilter('expiring') : undefined,
          selected: stockFilter === 'expiring',
          accessibilityLabel: 'Por vencer — filtrar productos próximos a vencer',
          testID: 'stock.kpi.expiring',
        },
        {
          label: 'Vencidos',
          value: quickViewCountsReady ? String(expired) : '—',
          tone: quickViewCountsReady && expired > 0 ? 'bad' : 'default',
          onPress: expired > 0 ? () => onToggleStockFilter('expired') : undefined,
          selected: stockFilter === 'expired',
          accessibilityLabel: 'Vencidos — filtrar productos con stock vencido',
          testID: 'stock.kpi.expired',
        },
      );
    }
    return items;
  }, [
    quickViewCountsReady,
    totalProducts,
    totalValue,
    coveragePercent,
    criticalCount,
    belowMin,
    expiringSoon,
    expired,
    showExpiryTracking,
    canViewValuation,
    stockFilter,
    onToggleStockFilter,
  ]);

  const kpiBlock = (
    <PaperSheet testID="stock.kpi.sheet">
      <TicketKpiStrip
        items={kpiItems}
        layout={layoutWide ? 'desk' : 'mobile'}
        variant="soft"
      />
    </PaperSheet>
  );

  /**
   * Error de KPIs ≠ inventario vacío: si ya hay snapshot previo seguimos
   * mostrándolo (stale) y este strip ofrece retry; si nunca cargó, los números
   * quedan en «—» y este strip explica por qué.
   */
  const kpiErrorBlock = kpisError ? (
    <View style={styles.kpiErrorRow} testID="stock.kpi.error">
      <Text style={styles.kpiErrorText}>
        {inventoryDashboard
          ? 'No se pudieron actualizar los indicadores.'
          : 'No se pudieron cargar los indicadores.'}
      </Text>
      {onRetryKpis ? (
        <TouchableOpacity
          onPress={onRetryKpis}
          disabled={kpisLoading}
          accessibilityRole="button"
          accessibilityLabel="Reintentar indicadores"
          testID="stock.kpi.retry"
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        >
          <Text style={styles.kpiErrorRetry}>
            {kpisLoading ? 'Cargando…' : 'Reintentar'}
          </Text>
        </TouchableOpacity>
      ) : null}
    </View>
  ) : null;

  const [moreActionsSheetOpen, setMoreActionsSheetOpen] = useState(false);

  /** Acciones secundarias — van al sheet "Más". */
  const sheetActions = useMemo((): TicketMoreAction[] => {
    const items: TicketMoreAction[] = [
      {
        key: 'transferir',
        label: 'Transferir',
        hint: canTransferBetweenBranches
          ? 'Entre sucursales'
          : 'Sumás otra sucursal en Organización.',
        icon: 'swap-horizontal-outline',
        onPress: onOpenTransferInfo,
        disabled: !canEdit || !canTransferBetweenBranches,
        testID: 'stock.transfer',
      },
      {
        key: 'conteo',
        label: 'Conteo físico',
        hint: 'Verificá cantidades contando',
        icon: 'clipboard-outline',
        onPress: onOpenPhysicalCount,
        disabled: !canEdit,
        testID: 'stock.physical-count',
      },
      {
        key: 'coeficiente',
        label: 'Coeficiente masivo',
        hint: 'Ajuste masivo de precios',
        icon: 'trending-up-outline',
        onPress: onOpenBulkCoefficient,
        disabled: !canEdit,
        testID: 'stock.bulk-coefficient',
      },
    ];
    if (onOpenCsv) {
      items.push({
        key: 'csv',
        label: 'Importar / Exportar CSV',
        hint: 'Importar o exportar',
        icon: 'document-text-outline',
        onPress: onOpenCsv,
        disabled: false,
        testID: 'stock.csv',
      });
    }
    // «Exportar listado» queda absorbido por el modal CSV como opción de alcance
    // (listado filtrado) — no duplicamos la acción acá.
    if (onOpenActivity) {
      items.push({
        key: 'activity',
        label: activityLoading ? 'Actividad reciente…' : 'Actividad reciente',
        hint: 'Historial de sucursal',
        icon: 'time-outline',
        onPress: onOpenActivity,
        disabled: false,
      });
    }
    return items;
  }, [
    canEdit,
    canTransferBetweenBranches,
    onOpenTransferInfo,
    onOpenPhysicalCount,
    onOpenBulkCoefficient,
    onOpenCsv,
    onOpenActivity,
    activityLoading,
  ]);

  const readOnlyBanner = !canEdit ? (
    <View style={posUi.readOnlyBanner}>
      <Text style={posUi.readOnlyText}>Solo lectura. Podés ver el stock, no cambiarlo.</Text>
    </View>
  ) : null;

  const lookupBlocks = lookupFeedback ? (
    <View style={posUi.checkoutStatusBox}>
      <Text style={posUi.lookupFeedback}>{lookupFeedback}</Text>
    </View>
  ) : null;

  const moreActionsSheet = (
    <TicketMoreActionsSheet
      visible={moreActionsSheetOpen}
      title="Acciones de stock"
      actions={sheetActions}
      onClose={() => setMoreActionsSheetOpen(false)}
    />
  );

  const primaryButton = (
    <TouchableOpacity
      style={[styles.deskBtnGold, !canEdit && posUi.disabledCard]}
      onPress={onOpenAddStock}
      disabled={!canEdit}
      activeOpacity={0.7}
      accessibilityRole="button"
      accessibilityLabel="Ingresar stock"
      accessibilityState={{ disabled: !canEdit }}
      testID="stock.add-stock"
    >
      <Text style={styles.deskBtnGoldText}>Ingresar stock</Text>
    </TouchableOpacity>
  );

  // ── Wide: proto = Ingresar stock arriba · tools = Buscar + Filtros + Más ──
  if (layoutWide) {
    return (
      <View style={styles.headerStack}>
        {readOnlyBanner}

        <TicketModuleHeader
          kicker="Stock"
          title="Inventario del local"
          meta="Qué tenés, qué falta y cuánto vale"
          testID="stock.header.sheet"
          trailing={primaryButton}
          tools={
            <View style={styles.deskToolsRow}>
              <View style={styles.deskSearchFlex}>
                <CatalogStockProductSearchFields
                  inputRef={searchInputRef}
                  value={searchQuery}
                  onChangeText={onSearchQueryChange}
                  branchId={branchId}
                  searchProductsFn={searchProducts}
                  posExcludeMisc
                  onPickSuggestion={
                    onPickStockSearchSuggestion
                      ? (row) => onPickStockSearchSuggestion(row as InventoryProductSearchRow)
                      : undefined
                  }
                  pinnedProduct={pinnedStockProduct}
                  // P2: copy explícito — el pin es un foco exclusivo que ignora
                  // status/categoría/marca/proveedor (los filtros quedan seteados
                  // y vuelven a aplicar al quitar el pin).
                  pinnedCaption="Viendo solo"
                  onClearPinnedProduct={onClearPinnedStockSearch}
                  onPressScan={onOpenScanner}
                  showBarcodeCameraButton={showBarcodeCameraButton}
                  placeholder={`Nombre, SKU o código (min. ${MIN_PRODUCT_SEARCH_QUERY_CHARS} caracteres)`}
                  placeholderCompact="Buscar producto"
                  compactTitleBreakpoint={400}
                  accessibilityLabel="Buscar producto en stock"
                  onSubmitEditing={onSearchSubmitEditing}
                  autoFocusOnWeb={false}
                  scanTone="neutral"
                  showHidHint={false}
                  unifiedBoxStyle={styles.deskSearchBox}
                  inputStyle={styles.deskSearchInput}
                  sectionLabel={null}
                  textInputProps={{ testID: 'stock.search' }}
                />
              </View>
              {filterTrigger}
              {sheetActions.length > 0 ? (
                <TouchableOpacity
                  style={styles.deskBtn}
                  onPress={() => setMoreActionsSheetOpen(true)}
                  accessibilityRole="button"
                  accessibilityLabel="Más acciones de stock"
                  testID="stock.more.open"
                >
                  <Text style={styles.deskBtnText}>Más</Text>
                </TouchableOpacity>
              ) : null}
            </View>
          }
        >
          {moreActionsSheet}
        </TicketModuleHeader>

        {kpiBlock}
        {kpiErrorBlock}
        {lookupBlocks}
      </View>
    );
  }

  // ── Mobile: proto = Buscar+Filtros+Más · Ingresar stock full · Más para secundarias ──
  return (
    <View style={styles.headerStack}>
      {readOnlyBanner}

      <TicketModuleHeader
        kicker="Stock"
        title="Inventario del local"
        meta="Qué tenés, qué falta y cuánto vale"
        testID="stock.header.sheet"
        tools={
          <>
            <View style={styles.searchRowMobile}>
              <View style={styles.searchBoxMobile}>
                <Ionicons name="search-outline" size={18} color={ticketColors.ink3} />
                <TextInput
                  ref={searchInputRef}
                  value={searchQuery}
                  onChangeText={onSearchQueryChange}
                  placeholder="Buscar producto…"
                  placeholderTextColor={ticketColors.ink3}
                  style={styles.searchInputMobile}
                  accessibilityLabel="Buscar producto en stock"
                  autoCorrect={false}
                  autoFocus={false}
                  returnKeyType={onSearchSubmitEditing ? 'search' : 'default'}
                  onSubmitEditing={onSearchSubmitEditing}
                  blurOnSubmit={false}
                  testID="stock.search"
                />
                {searchQuery.trim().length > 0 ? (
                  <TouchableOpacity
                    style={styles.searchClearMobile}
                    onPress={() => onSearchQueryChange('')}
                    accessibilityRole="button"
                    accessibilityLabel="Limpiar búsqueda"
                  >
                    <Ionicons name="close-circle" size={20} color={ticketColors.ink2} />
                  </TouchableOpacity>
                ) : null}
              </View>
              {showBarcodeCameraButton && onOpenScanner ? (
                <TouchableOpacity
                  style={styles.scanBtnMobile}
                  onPress={onOpenScanner}
                  accessibilityRole="button"
                  accessibilityLabel="Escanear código de barras"
                >
                  <Ionicons name="barcode-outline" size={22} color={ticketColors.ink2} />
                </TouchableOpacity>
              ) : null}
            </View>
            <View style={styles.toolsRowMobile}>
              {filterTrigger}
              {sheetActions.length > 0 ? (
                <TouchableOpacity
                  style={styles.actionMoreMobile}
                  onPress={() => setMoreActionsSheetOpen(true)}
                  activeOpacity={0.6}
                  accessibilityRole="button"
                  accessibilityLabel="Más acciones de stock"
                  accessibilityHint="Transferir, conteo físico, coeficiente masivo, importar o exportar CSV y actividad reciente"
                  testID="stock.more.open"
                >
                  <Text style={styles.moreBtnText}>Más</Text>
                </TouchableOpacity>
              ) : null}
            </View>

            <TouchableOpacity
              style={[styles.actionPrimaryMobile, !canEdit && posUi.disabledCard]}
              onPress={onOpenAddStock}
              disabled={!canEdit}
              activeOpacity={0.7}
              accessibilityRole="button"
              accessibilityLabel="Ingresar stock"
              accessibilityState={{ disabled: !canEdit }}
              testID="stock.add-stock"
            >
              <Text style={styles.actionPrimaryTextMobile}>Ingresar stock</Text>
            </TouchableOpacity>

            {pinnedStockProduct?.label && onClearPinnedStockSearch ? (
              <TouchableOpacity
                style={styles.pinnedChipMobile}
                onPress={onClearPinnedStockSearch}
                activeOpacity={0.6}
                accessibilityRole="button"
                accessibilityLabel="Quitar filtro de producto"
              >
                <Text style={styles.pinnedChipTextMobile} numberOfLines={1}>
                  {`Solo ${pinnedStockProduct.label}`}
                </Text>
                <Ionicons name="close-circle" size={16} color={ticketColors.ink3} />
              </TouchableOpacity>
            ) : null}
          </>
        }
      />

      {kpiBlock}
      {kpiErrorBlock}

      {moreActionsSheet}

      {lookupBlocks}
    </View>
  );
};

const styles = StyleSheet.create({
  headerStack: {
    gap: 10,
    paddingBottom: spacing.sm,
  },
  kpiErrorRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    backgroundColor: ticketColors.warnBg,
    borderColor: ticketColors.warnBorder,
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  kpiErrorText: {
    flex: 1,
    fontSize: 13,
    fontWeight: '500',
    color: ticketColors.ink2,
  },
  kpiErrorRetry: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.brandPrimary,
  },
  deskToolsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
  },
  deskSearchFlex: {
    flex: 1,
    minWidth: 180,
  },
  deskSearchBox: {
    backgroundColor: ticketColors.paper2,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    // P2 a11y: ≥44px de alto (antes 42) — target táctil/click mínimo.
    height: 44,
    minHeight: 44,
    paddingVertical: 0,
    paddingHorizontal: 12,
    gap: 0,
  },
  deskSearchInput: {
    fontSize: 14,
    fontWeight: '500',
    fontFamily: ticketFonts.sans,
    color: ticketColors.ink,
    height: 44,
    paddingVertical: 0,
  },
  deskBtn: {
    height: 44,
    paddingHorizontal: 14,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  deskBtnText: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  deskBtnGold: {
    height: 44,
    paddingHorizontal: 14,
    borderRadius: 10,
    backgroundColor: ticketColors.gold,
    alignItems: 'center',
    justifyContent: 'center',
  },
  deskBtnGoldText: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.goldInk,
    fontFamily: ticketFonts.sans,
  },
  searchRowMobile: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  toolsRowMobile: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  searchBoxMobile: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    height: 44,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    paddingHorizontal: 12,
  },
  searchInputMobile: {
    flex: 1,
    fontSize: 14,
    fontWeight: '500',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
    paddingVertical: 0,
  },
  // P2 a11y: clear-button ≥44px (antes solo el ícono ~20px).
  // marginRight negativa compensa el paddingHorizontal del box para que el
  // ícono quede en la misma posición visual.
  searchClearMobile: {
    width: 44,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: -10,
  },
  scanBtnMobile: {
    width: 44,
    height: 44,
    borderRadius: 10,
    backgroundColor: ticketColors.paper2,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionMoreMobile: {
    height: 44,
    paddingHorizontal: 14,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  moreBtnText: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  actionPrimaryMobile: {
    height: 44,
    borderRadius: 10,
    backgroundColor: ticketColors.gold,
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionPrimaryTextMobile: {
    fontSize: 14,
    fontWeight: '800',
    color: ticketColors.goldInk,
    fontFamily: ticketFonts.sans,
  },
  pinnedChipMobile: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.md,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: ticketColors.paper2,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: ticketColors.ruleSoft,
    alignSelf: 'flex-start',
  },
  pinnedChipTextMobile: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '600',
  },
});
